import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHotel } from '@/hooks/useHotel';
import { getFieldByLang } from '@/utils/common';
import Logo from '@/assets/image/THE_MOON.jpg';

const Footer = () => {
  const { t } = useTranslation();
  const { hotels } = useHotel();
  const quickLinks = t('navigation', { returnObjects: true }) as {
    name: string;
    href: string;
  }[];

  const legalLinks = [
    { name: t('footer.privacyPolicy'), href: '/privacy' },
    { name: t('footer.termsOfUse'), href: '/terms' },
  ];

  return (
    <footer className="bg-primary-dark text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 sm:gap-10">
          <div>
            <Link to="/" className="flex items-center space-x-3 mb-5">
              <div className="w-10 h-10 bg-accent-gold rounded-full flex items-center justify-center shadow-sm overflow-hidden">
                <img
                  src={Logo}
                  alt="Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <span
                  className="text-xl font-bold"
                  style={{ fontFamily: '"Pacifico", serif' }}
                >
                  The Moon Hotel
                </span>
              </div>
            </Link>

            <p className="text-gray-300 text-sm leading-relaxed mb-5">
              {t('footer.description')}
            </p>

            <div className="flex space-x-3">
              {[
                {
                  icon: 'ri-facebook-fill',
                  href: 'https://facebook.com/accordhotel',
                },
                {
                  icon: 'ri-instagram-line',
                  href: 'https://instagram.com/accordhotel',
                },
                {
                  icon: 'ri-mail-line',
                  href: 'mailto:sales.themoonhotel@gmail.com',
                },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-800 hover:bg-accent-gold rounded-lg flex items-center justify-center transition-all duration-300"
                >
                  <i className={`${social.icon} text-white text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-semibold mb-4 text-accent-gold uppercase tracking-wide">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-300 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Hotel Branches */}
          <div>
            <h3 className="text-base font-semibold mb-4 text-accent-gold uppercase tracking-wide">
              {t('footer.hotel')}
            </h3>
            <ul className="space-y-3">
              {hotels.map((hotel, index) => (
                <li key={index}>
                  <div className="text-white font-medium text-sm">
                    {getFieldByLang(hotel, 'name')}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {getFieldByLang(hotel, 'address')}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base font-semibold mb-4 text-accent-gold uppercase tracking-wide">
              {t('footer.contact')}
            </h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start space-x-3">
                <i className="ri-phone-line text-accent-gold mt-1 text-lg"></i>
                <div>
                  <div className="text-white font-medium">
                    {t('footer.hotlineLabel')}
                  </div>
                  <a
                    href="tel:0933537986"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {t('footer.hotline')}
                  </a>
                  <p className="text-gray-400 text-xs">{t('footer.support')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <i className="ri-mail-line text-accent-gold mt-1 text-lg"></i>
                <div>
                  <div className="text-white font-medium">
                    {t('footer.emailLabel')}
                  </div>
                  <a
                    href="mailto:sales.themoonhotel@gmail.com"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {t('footer.email')}
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-line text-accent-gold mt-1 text-lg"></i>
                <div>
                  <div className="text-white font-medium">
                    {t('footer.addressLabel')}
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    {t('footer.address')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-center text-center space-y-3 sm:space-y-0 text-sm text-gray-400">
            <div>{t('footer.copyright')}</div>
            <div className="flex items-center space-x-4">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="hover:text-accent-gold transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
